import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePutMeNotificationSettings } from '@/api/auth/self/self';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * Communication preference event names
 */
const COMMUNICATION_EVENTS = {
  productUpdates: 'ProductUpdates',
  newsTrainingArticles: 'NewsTrainingArticles',
} as const;

type CommunicationEvent = (typeof COMMUNICATION_EVENTS)[keyof typeof COMMUNICATION_EVENTS];

interface CommunicationPreferencesStepProps {
  onNext: () => void;
  onBack?: () => void;
}

/**
 * Step for setting communication preferences during onboarding.
 * Allows users to opt-in to product updates and news/training emails.
 */
export function CommunicationPreferencesStep({ onNext, onBack }: CommunicationPreferencesStepProps) {
  const { t } = useLanguage();

  // Local state for preferences (default to false/off)
  const [settings, setSettings] = useState<Record<CommunicationEvent, boolean>>({
    [COMMUNICATION_EVENTS.productUpdates]: false,
    [COMMUNICATION_EVENTS.newsTrainingArticles]: false,
  });

  const [isSaving, setIsSaving] = useState(false);

  const updateMutation = usePutMeNotificationSettings();

  /**
   * Handle toggle change for a preference
   */
  const handleToggle = (eventName: CommunicationEvent, checked: boolean) => {
    setSettings((prev) => ({
      ...prev,
      [eventName]: checked,
    }));
  };

  /**
   * Save preferences and proceed to next step
   */
  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // Convert settings to API format
      const settingsArray = Object.entries(settings).map(([eventName, isEnabled]) => ({
        eventName,
        isEnabled,
      }));

      await updateMutation.mutateAsync({
        data: {
          settings: settingsArray,
        },
      });

      onNext();
    } catch (error) {
      console.error('Failed to save communication preferences:', error);
      toast.error(t.userOnboardingWizard.communicationPreferencesStep.saveError);
    } finally {
      setIsSaving(false);
    }
  };

  const preferenceOptions = [
    {
      eventName: COMMUNICATION_EVENTS.productUpdates,
      label: t.userOnboardingWizard.communicationPreferencesStep.productUpdates.label,
      description: t.userOnboardingWizard.communicationPreferencesStep.productUpdates.description,
    },
    {
      eventName: COMMUNICATION_EVENTS.newsTrainingArticles,
      label: t.userOnboardingWizard.communicationPreferencesStep.newsTrainingArticles.label,
      description: t.userOnboardingWizard.communicationPreferencesStep.newsTrainingArticles.description,
    },
  ];

  return (
    <Card className="h-[480px] flex flex-col">
      <CardHeader>
        <CardTitle>{t.userOnboardingWizard.communicationPreferencesStep.title}</CardTitle>
        <CardDescription>{t.userOnboardingWizard.communicationPreferencesStep.description}</CardDescription>
      </CardHeader>
      <form onSubmit={(e) => { void handleContinue(e); }} className="flex flex-col flex-1">
        <CardContent className="space-y-4 flex-1">
          <div className="space-y-4">
            {preferenceOptions.map((option) => (
              <div
                key={option.eventName}
                className="flex items-start justify-between gap-4 rounded-md border p-4"
              >
                <div className="space-y-0.5">
                  <Label htmlFor={option.eventName} className="text-sm font-medium">
                    {option.label}
                  </Label>
                  <p className="text-sm text-muted-foreground">{option.description}</p>
                </div>
                <Switch
                  id={option.eventName}
                  checked={settings[option.eventName]}
                  onCheckedChange={(checked) => { handleToggle(option.eventName, checked); }}
                  disabled={isSaving}
                />
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter className={`flex pt-4 ${onBack ? 'justify-between' : 'justify-end'}`}>
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack} disabled={isSaving}>
              {t.userOnboardingWizard.communicationPreferencesStep.back}
            </Button>
          )}
          <Button type="submit" disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.userOnboardingWizard.communicationPreferencesStep.saving}
              </>
            ) : (
              t.userOnboardingWizard.communicationPreferencesStep.continue
            )}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
